import type { Category, Product } from '@interfaces/general';
import type { DetectedCategory, DetectedProduct } from '@pages/Menu/Products/MenuUploaderV2/types/menuUpload.types';

import { normalize, isPriceClose } from './normalize';

// Matching client-side del menú ESCANEADO contra el catálogo EXISTENTE del
// comercio (WS-B). Reusa `normalize()` (mismo criterio que el dedup intra-batch):
// match EXACTO sobre el nombre normalizado — NADA de fuzzy, para no asociar por
// error categorías reales del comercio (decisión D1 del plan). Es una capa
// overlay: NO toca `consolidate()`.

/**
 * Construye el mapa de asociación categoría DETECTADA → categoría EXISTENTE.
 * Solo considera categorías existentes ACTIVAS (filtra internamente, R4): asociar
 * a una inactiva metería productos en una categoría oculta.
 *
 * @param {DetectedCategory[]} detected Categorías detectadas por la IA (ids sintéticos).
 * @param {Category[]} existing Catálogo COMPLETO del comercio (se filtra `estatus==='activo'`).
 * @returns {Record<string, string>} Mapa `detectedCategoryId -> existingCategoryId` (match exacto-normalizado).
 */
export const buildExistingCategoryLinks = (
  detected: DetectedCategory[],
  existing: Category[],
): Record<string, string> => {
  // Índice nombre-normalizado → id existente (primera ocurrencia gana, determinista).
  const byNormalizedName = new Map<string, string>();
  for (const cat of existing) {
    if (cat.estatus !== 'activo') continue;
    const key = normalize(cat.nombre);
    if (!key || byNormalizedName.has(key)) continue;
    byNormalizedName.set(key, cat.id);
  }

  const links: Record<string, string> = {};
  for (const cat of detected) {
    const key = normalize(cat.name);
    if (!key) continue;
    const existingId = byNormalizedName.get(key);
    if (existingId) links[cat.id] = existingId;
  }
  return links;
};

export interface ProductDuplicateHint {
  detectedProductId: string;
  existingProductName: string;
  /** Precio del producto existente (de su 1ª presentación) o null si no tiene. */
  existingPrice: number | null;
  /** true si el precio detectado y el existente están "cerca" (±10%, `isPriceClose`). */
  priceClose: boolean;
}

const firstPriceOfExisting = (p: Product): number | null => {
  const precio = p.opciones?.[0]?.precio;
  return typeof precio === 'number' ? precio : null;
};

const firstPriceOfDetected = (p: DetectedProduct): number | null => {
  if (typeof p.price === 'number') return p.price;
  const precio = p.opciones?.[0]?.precio;
  return typeof precio === 'number' ? precio : null;
};

/**
 * Detecta productos escaneados que probablemente YA EXISTEN en el comercio,
 * acotando la búsqueda a la categoría existente ASOCIADA (vía `links`). Es solo
 * un AVISO (decisión #4: marca, no mergea ni deselecciona).
 *
 * @param {DetectedProduct[]} detected Productos detectados por la IA.
 * @param {Map<string, Product[]>} existingByCategory Productos existentes agrupados por `id_categoria`.
 * @param {Record<string, string>} links Mapa detectedCategoryId → existingCategoryId (de `buildExistingCategoryLinks`).
 * @returns {Record<string, ProductDuplicateHint>} Mapa `detectedProductId -> hint` (solo los que matchean por nombre).
 */
export const findExistingProductDuplicates = (
  detected: DetectedProduct[],
  existingByCategory: Map<string, Product[]>,
  links: Record<string, string>,
): Record<string, ProductDuplicateHint> => {
  // R4 — Índice por categoría: `normalizedName -> Product` (primera ocurrencia), construido
  // una sola vez por categoría asociada y memoizado. Evita el `find` lineal por cada producto
  // detectado (antes O(detectados × existentes-por-categoría) en CADA recomputo del memo, que
  // se dispara en cada edición de producto → lag con catálogos grandes).
  const indexByCategory = new Map<string, Map<string, Product>>();
  const indexFor = (categoryId: string): Map<string, Product> => {
    let idx = indexByCategory.get(categoryId);
    if (!idx) {
      idx = new Map();
      for (const p of existingByCategory.get(categoryId) ?? []) {
        const k = normalize(p.nombre);
        if (k && !idx.has(k)) idx.set(k, p);
      }
      indexByCategory.set(categoryId, idx);
    }
    return idx;
  };

  const hints: Record<string, ProductDuplicateHint> = {};
  for (const prod of detected) {
    // Solo tiene sentido avisar si la categoría del producto está asociada a una
    // existente: el duplicado se busca DENTRO de esa categoría existente.
    const linkedExistingCatId = prod.category ? links[prod.category] : undefined;
    if (!linkedExistingCatId) continue;

    const key = normalize(prod.name);
    if (!key) continue;

    const match = indexFor(linkedExistingCatId).get(key);
    if (!match) continue;

    const existingPrice = firstPriceOfExisting(match);
    const detectedPrice = firstPriceOfDetected(prod);
    const priceClose =
      existingPrice !== null && detectedPrice !== null ? isPriceClose(detectedPrice, existingPrice) : false;

    hints[prod.id] = {
      detectedProductId: prod.id,
      existingProductName: match.nombre,
      existingPrice,
      priceClose,
    };
  }
  return hints;
};
